import React from 'react';

import Tooltip from '@atlaskit/tooltip';

import { IssueStatus } from '@qality/common-components/src/atoms/IssueStatus';
import { Text } from '@qality/common-components/src/atoms/Text';
import { FEATURE_FLAG, IS_CLOUD } from '@qality/common-config';
import { TestType } from '@qality/common-model';
import StorageOfUniqueServices from '@qality/common-service/src/StorageOfUniqueServices';
import { useOverflowObserver } from '@qality/common-utils';
import { DEFAULT_TEST_TYPE } from '@qality/common-utils/src/TestType.const';

import IssueTestType from '../IssueTestType/IssueTestType';
import {
  ContainerStyled,
  IssueContentWrapperStyled,
  IssueIconStyled,
  IssueLinkStyled,
  IssueSpanDescriptionStyled,
  IssueSpanKeyStyled,
  IssueStyled,
} from './Issue.styled';

export type TestTypeProps = {
  testType?: TestType;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isIconOnly?: boolean;
  style?: React.CSSProperties;
};

export type IssueProps = {
  iconSrc?: string;
  title?: string;
  issueKey?: string;
  issueLink?: string;
  status?: any;
  subTitle?: string;
  shouldOpenInCurrentTab?: boolean;
  shouldAddStrikethrough?: boolean;
  addPadding?: boolean;
  withEllipsis?: boolean;
  testTypeProps?: TestTypeProps;
};

export const Issue = ({
  iconSrc,
  title,
  issueKey,
  issueLink,
  status,
  subTitle,
  shouldOpenInCurrentTab,
  shouldAddStrikethrough,
  addPadding = false,
  withEllipsis = false,
  testTypeProps,
}: IssueProps) => {
  const { ref, isOverflowing } = useOverflowObserver({
    isDisabled: !withEllipsis,
  });
  const strikethrough =
    status?.kind === 'success' && shouldAddStrikethrough ? 'line-through' : '';

  const handleOpenIssue = async (e) => {
    e.stopPropagation();
    if (shouldOpenInCurrentTab) {
      StorageOfUniqueServices.get('AP').navigator.go('site', {
        absoluteUrl: issueLink,
      });
    }
  };

  const targetOption = shouldOpenInCurrentTab ? null : { target: '_BLANK' };
  const tooltipContent =
    isOverflowing && [issueKey, title, subTitle].filter(Boolean).join(' ');

  const showTestType = testTypeProps !== undefined;
  return (
    <ContainerStyled withEllipsis={withEllipsis}>
      <Tooltip content={tooltipContent}>
        <IssueStyled data-testid='Issue' alignCenter={true}>
          {iconSrc && (
            <IssueIconStyled height={subTitle ? 'medium' : 'small'}>
              <img src={iconSrc} alt='' />
            </IssueIconStyled>
          )}
          {showTestType && (
            <div style={testTypeProps.style}>
              <IssueTestType
                testType={
                  !FEATURE_FLAG.isPremium || !IS_CLOUD
                    ? DEFAULT_TEST_TYPE
                    : testTypeProps.testType
                }
                isLoading={testTypeProps.isLoading}
                isIconOnly={testTypeProps.isIconOnly}
                isReadOnly={testTypeProps.isReadOnly}
              />
            </div>
          )}
          <IssueContentWrapperStyled
            noPadding={!(iconSrc || addPadding || testTypeProps?.testType)}
            withEllipsis={withEllipsis}
            innerRef={ref}
          >
            {issueLink ? (
              <>
                {issueKey && (
                  <IssueLinkStyled
                    onClick={handleOpenIssue}
                    href={issueLink}
                    {...targetOption}
                  >
                    <IssueSpanKeyStyled strikethrough={strikethrough}>
                      {issueKey}
                    </IssueSpanKeyStyled>
                  </IssueLinkStyled>
                )}
                <IssueLinkStyled
                  onClick={handleOpenIssue}
                  href={issueLink}
                  {...targetOption}
                >
                  <IssueSpanDescriptionStyled>
                    {title}
                  </IssueSpanDescriptionStyled>
                  {status && (
                    <IssueStatus kind={status.kind}>{status.name}</IssueStatus>
                  )}
                </IssueLinkStyled>
              </>
            ) : (
              <>
                {issueKey && (
                  <IssueSpanKeyStyled strikethrough={strikethrough}>
                    {issueKey}
                  </IssueSpanKeyStyled>
                )}
                <IssueSpanDescriptionStyled>{title}</IssueSpanDescriptionStyled>
                {status && (
                  <IssueStatus kind={status.kind}>{status.name}</IssueStatus>
                )}
                {subTitle && (
                  <Text inactive fontSize='xsmall'>
                    {subTitle}
                  </Text>
                )}
              </>
            )}
          </IssueContentWrapperStyled>
        </IssueStyled>
      </Tooltip>
    </ContainerStyled>
  );
};
